import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getHeatMapMerchants } from "../api/heat-map.api";
import type { MerchantModel } from "../types/Merchant";
import type { DashboardFilterPayload } from "../utils/heat-map.utils";

export const useHeatMapMerchants = (
  payload: DashboardFilterPayload,
  options?: { enabled?: boolean },
) => {
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<MerchantModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchData = useCallback(async () => {
    if (!enabled) return undefined;

    try {
      setError(null);
      const result = await runLatestRequest((signal) => getHeatMapMerchants(payload, signal));

      if (!result) return undefined;

      setData(result);
      return result;
    } catch (fetchError) {
      setData(null);
      setError(getApiErrorMessage(fetchError, "Failed to load merchants"));
      return undefined;
    }
  }, [enabled, payload, runLatestRequest]);

  useEffect(() => {
    if (enabled) {
      void fetchData();
    }
  }, [enabled, fetchData]);

  return { data, loading: enabled && loading, error, refetch: fetchData };
};
